/* eslint-disable camelcase */
import React, { useState } from 'react'

import { datadogRum } from '@datadog/browser-rum'
import {
  Box,
  Modal,
  useModal,
  ModalClose,
  ModalHeader,
  Heading3,
  ModalBody,
  Display,
  FlexDirection,
  Button,
  ButtonColorVariant,
} from '@gousto-internal/citrus-react'
import { ModalFooter } from 'ModalComponent'
import { useDispatch, useSelector } from 'react-redux'
import useSWR from 'swr'

import { sidesFetcher } from '@library/api-product'
import { queueTrackingEvent } from '@library/tracking-integration'

import type { BasketOrderDetails } from 'reducers/basket'
import { basketUpdateProducts } from 'routes/Menu/actions/basket'
import {
  getBasketOrderDetails,
  getBasketOrderId,
  getBasketProducts,
  getBasketSaveRequired,
} from 'selectors/basket'
import { getBasketSavePending } from 'selectors/status'

import { SidesAllergens } from './SidesAllergens'
import { SidesButton } from './SidesButton'
import { SidesError } from './SidesError'
import { SidesSkeleton } from './SidesSkeleton'
import { SidesTile } from './SidesTile'
import type {
  SidesModalCloseEvent,
  SidesModalContinueEvent,
  ViewSidesAllergensEvent,
  ViewSidesModalEvent,
} from './trackingEvents'

export const sidesModalName = 'SidesModal'

export const SidesModal = () => {
  const dispatch = useDispatch()
  const { closeCurrentModal } = useModal()

  const orderDetails = useSelector(getBasketOrderDetails) as BasketOrderDetails
  const orderId = useSelector(getBasketOrderId)
  const basketProducts = useSelector(getBasketProducts)
  const basketSaveRequired = useSelector(getBasketSaveRequired)
  const basketSavePending = useSelector(getBasketSavePending)
  const [isShowingAllergens, setIsShowingAllergens] = useState(false)

  const recipeItems = orderDetails.get('recipeItems')
  const recipeIds = recipeItems.map((item: any) => item.get('recipeUuid')).toArray()

  const { data, error, mutate } = useSWR(
    () =>
      recipeIds
        ? {
            periodId: orderDetails.getIn(['period', 'id']),
            recipes: recipeIds,
          }
        : null,
    sidesFetcher,
  )

  const totalSidesCost = React.useMemo(() => {
    if (!data?.data) return 0

    return data.data.reduce((acc, { list_price, id }) => {
      const quantity = Number(basketProducts.get(id) || 0)

      return acc + Number(list_price) * quantity
    }, 0)
  }, [data?.data, basketProducts])

  const sidesPresentedIds = data?.data.map(({ id }) => id) ?? []
  const sidesSelectedIds = sidesPresentedIds.filter((id) => Number(basketProducts.get(id) ?? 0) > 0)

  const sidesPresentedCount = sidesPresentedIds.length
  const sidesSelectedCount = sidesSelectedIds.length

  const handleContinue = async () => {
    datadogRum.addAction('close_sides_modal', {
      sidesPresentedCount,
      sidesSelectedCount,
    })

    queueTrackingEvent<SidesModalContinueEvent>({
      action: 'sides_modal_continue',
      properties: {
        sides_presented: sidesPresentedIds,
        sides_selected: sidesSelectedIds,
        total_sides_cost: totalSidesCost,
        order_updated: basketSaveRequired,
      },
    })

    if (basketSaveRequired) {
      await dispatch(basketUpdateProducts({ isOrderConfirmation: true }))
    }

    closeCurrentModal()
  }

  const showAllergens = (show: boolean) => {
    queueTrackingEvent<ViewSidesAllergensEvent>({
      action: 'view_sides_allergens',
      properties: {
        show,
      },
    })
    setIsShowingAllergens(show)
  }

  const closeModal = () => {
    datadogRum.addAction('close_sides_modal', {
      sidesPresentedCount,
      sidesSelectedCount,
    })

    queueTrackingEvent<SidesModalCloseEvent>({
      action: 'sides_modal_close',
      properties: {
        sides_presented: sidesPresentedIds,
        sides_selected: sidesSelectedIds,
        total_sides_cost: totalSidesCost,
        order_updated: false,
      },
    })
    closeCurrentModal()
  }

  const handleClose = () => {
    if (isShowingAllergens) {
      showAllergens(false)
    } else {
      closeModal()
    }
  }

  const trackModalOpen = () => {
    datadogRum.addAction('view_sides_modal')

    queueTrackingEvent<ViewSidesModalEvent>({
      action: 'view_sides_modal',
      properties: {
        order_id: orderId!,
      },
    })
  }

  return (
    <Modal isOpen name={sidesModalName} onAfterOpen={trackModalOpen} onRequestClose={closeModal}>
      <ModalHeader>
        <Heading3>
          {isShowingAllergens ? 'Allergens and nutrition' : 'Your recipes go well with...'}
        </Heading3>
        <ModalClose onClose={handleClose} />
      </ModalHeader>
      <ModalBody>
        {error && <SidesError onRetry={mutate} />}
        {!error && !data && <SidesSkeleton />}
        {!error &&
          data &&
          (isShowingAllergens ? (
            <SidesAllergens sides={data.data} />
          ) : (
            <Box display={Display.Flex} flexDirection={FlexDirection.Column} gap={2}>
              {data.data.map(({ id, images, list_price, stock, title }) => (
                <SidesTile
                  key={id}
                  id={id}
                  images={images}
                  listPrice={list_price}
                  stock={stock}
                  title={title}
                  quantity={basketProducts.get(id) ?? 0}
                />
              ))}
            </Box>
          ))}
      </ModalBody>
      <ModalFooter>
        {isShowingAllergens ? (
          <Button height={50} onClick={() => showAllergens(false)}>
            Back to sides
          </Button>
        ) : (
          <Box display={Display.Flex} flexDirection={FlexDirection.Column} gap={1}>
            {!error && data && (
              <Button
                colorVariant={ButtonColorVariant.Tertiary}
                height={50}
                onClick={() => showAllergens(true)}
              >
                See allergens and nutrition
              </Button>
            )}
            <SidesButton
              disabled={basketSavePending}
              onClick={handleContinue}
              totalPrice={totalSidesCost}
            />
          </Box>
        )}
      </ModalFooter>
    </Modal>
  )
}
